/**
 * City landing page title with a live stock count — "27 Used Taxis for Sale in
 * Gurugram – T-Permit" — the pattern Droom's /taxis pages win clicks with.
 *
 * ONE rule for both writers, like blogJsonLd.js:
 *   - pages/buyer/CityLanding.js applies it at runtime once the count loads, and
 *   - scripts/inject-seo-heads.js applies it at build time from the same API
 *     query, so the static head and the rendered page cannot disagree.
 *
 * The count is never hardcoded. A number frozen into CITY_SEO goes stale the
 * day a car sells, which is the stale-year-table trap. Below the ≥3 local-stock
 * gate (seo-plan-2026-09-07) the static CITY_SEO title stands: "1 Used Taxi for
 * Sale in Noida" advertises exactly the thin inventory the gate exists to hide.
 *
 * scripts/inject-seo-heads.js loads THIS file by stripping the `export` keywords
 * and requiring it, so keep every declaration a plain top-level export and keep
 * the file free of imports.
 */
export const CITY_COUNT_MIN = 3;

/**
 * Same rule for the unfiltered /vehicles page, where the number is the whole
 * live catalogue rather than one city's. Kept here so there is one definition
 * of "show the count only when there is a real one".
 */
export function inventoryTitle(staticTitle, count) {
  const n = Number(count);
  if (!Number.isInteger(n) || n < CITY_COUNT_MIN) return staticTitle;
  return `${n} Used Taxis for Sale in India – Buy Taxi Car`;
}

export function cityTitle(staticTitle, cityName, count, noindex) {
  const n = Number(count);
  if (noindex || !Number.isInteger(n) || n < CITY_COUNT_MIN) return staticTitle;
  return `${n} Used Taxis for Sale in ${cityName} – T-Permit`;
}

// 249000 -> "2.49", 200000 -> "2", 230000 -> "2.3". Exact, never rounded down:
// "from ₹2.4 lakh" when the cheapest car is ₹2.49 lakh would undersell reality.
export function lakhFrom(price) {
  const n = Number(price);
  if (!isFinite(n) || n <= 0) return '';
  return (n / 100000).toFixed(2).replace(/\.?0+$/, '');
}

/**
 * City meta description led by the live count and the cheapest live car —
 * "28 used taxis in Delhi from ₹2 lakh — CNG, diesel & EV…". Same gate and same
 * API query as cityTitle(); the handwritten CITY_SEO description supplies the
 * rest, with its own "Buy used taxis in <City> – " opener dropped so the two
 * don't say the same thing twice. Below the gate it is returned unchanged.
 */
export function cityDescription(staticDesc, cityName, count, minPrice, noindex) {
  const n = Number(count);
  const from = lakhFrom(minPrice);
  if (noindex || !Number.isInteger(n) || n < CITY_COUNT_MIN || !from) return staticDesc;
  const opener = new RegExp(`^Buy used taxis in ${cityName}\\s*[–-]\\s*`, 'i');
  const rest = String(staticDesc || '').replace(opener, '');
  return `${n} used taxis in ${cityName} from ₹${from} lakh — ${rest}`;
}
